/* eslint-disable static-service/require-stories -- we have stories for the Markdown component */
import {StyleSheet} from "aphrodite";

import {pureMarkdownRules} from "@khanacademy/pure-markdown";
import SimpleMarkdown from "@khanacademy/simple-markdown";
import {MathJax as TeX} from "@repo/component-tex";

import type {MarkdownRule} from "../types.ts";

import {blockStyle} from "./shared-styles.ts";

const MATH_INLINE_R = /^(?:\\+)\(((?:(?:(?!\\\))[\s\S])*)?)\\\)/;
const MATH_BLOCK_R =
    /^(?:\\+)\[((?:(?:(?!\\\])[\s\S])*)?)\\\](?: *)(?:\n *)*(?:\n|$)?/;

// Output rule for handling TeX expressions delimited by block delimiters
// \[ ... \]. Although $$ is also valid, we discourage using it in our markdown
// as it can be a source of many false positive matches..
export const mathBlockRule: Readonly<MarkdownRule> = {
    ...pureMarkdownRules.math,
    // Change the priority of the math rule since our delimiters are \[ & \],
    // we don't want it to be considered escaped square brackets.
    order: pureMarkdownRules.escape.order - 0.5,
    match: SimpleMarkdown.anyScopeRegex(MATH_BLOCK_R),
    parse: (capture, parse, state): any => {
        return {
            content: capture[1],
        };
    },
    react: (node, output, state) => (
        <TeX
            key={state.key}
            style={styles.mathBlock}
            contextForAnalytics="ai-guide"
        >
            {node.content}
        </TeX>
    ),
};

// Output rule for handling TeX expressions delimited by inline delimiters
// \( ... \). Although $ is also valid, we discourage using it in our
// markdown as it can be a source of many false positive matches.
export const mathRule: Readonly<MarkdownRule> = {
    ...pureMarkdownRules.math,
    // Change the priority of the math rule since our delimiters are \( & \),
    // we don't want it to be considered escaped parenthesis. pureMarkdownRules
    // has this rule ordered before `link` but that may not be necessary
    // now with these alternative delimiters.
    order: mathBlockRule.order + 0.1,
    match: SimpleMarkdown.anyScopeRegex(MATH_INLINE_R),
    parse: (capture, parse, state): any => {
        return {
            content: capture[1],
        };
    },
    react: (node, output, state) => (
        <TeX
            key={state.key}
            style={styles.mathInline}
            contextForAnalytics="ai-guide"
        >
            {node.content}
        </TeX>
    ),
};

const styles = StyleSheet.create({
    mathInline: {
        display: "inline-block",
        color: "inherit",
        // top/bottom margins added to provide some space between math on
        // multiple lines.
        margin: "0.25em 0",
    },
    mathBlock: {
        display: "block",
        color: "inherit",
        textAlign: "center",
        ...blockStyle,
    },
});
